import { apiSlice } from '../../../store/api/apiSlice';

export interface MemberReflectionBackfillScope {
  member_ids?: string[];
  session_ids?: string[];
  limit?: number;
}

export interface MemberReflectionBackfillSummary {
  total_members: number;
  transcript_bearing_sessions: number;
  generation_ready_sessions: number;
  readiness_gap_sessions: number;
  reflected_sessions: number;
  needs_backfill_sessions: number;
  queued_sessions: number;
  running_sessions: number;
  failed_sessions: number;
  blocked_sessions: number;
  latest_session_missing: number;
  latest_session_readiness_gaps: number;
  counts_by_status: Record<string, number>;
}

export interface MemberReflectionBackfillMemberRow {
  member_id: string;
  member_email: string;
  member_name: string;
  transcript_bearing_sessions: number;
  generation_ready_sessions: number;
  readiness_gap_sessions: number;
  reflected_sessions: number;
  needs_backfill_sessions: number;
  queued_sessions: number;
  running_sessions: number;
  failed_sessions: number;
  blocked_sessions: number;
  latest_session_id?: string;
  latest_session_at?: string;
  latest_session_status: string;
  latest_session_missing_reflection: boolean;
  latest_session_readiness_missing_or_stale: boolean;
  counts_by_status: Record<string, number>;
}

export interface MemberReflectionBackfillSessionRow {
  member_id: string;
  member_email: string;
  member_name: string;
  session_id: string;
  session_at: string;
  latest_session: boolean;
  coach_id: string;
  transcript_count: number;
  status: string;
  eligibility_status: string;
  readiness_version?: number;
  input_set_hash?: string;
  job_id?: string;
  job_status?: string;
  artifact_id?: string;
  blocked_reason?: string;
  error_message?: string;
  ready_transition_id?: string;
}

export interface MemberReflectionBackfillReadinessGapResponse {
  rows: MemberReflectionBackfillSessionRow[];
  count: number;
}

export interface MemberReflectionBackfillPrepareRequest extends MemberReflectionBackfillScope {}

export interface MemberReflectionBackfillPrepareResponse {
  evaluated_count: number;
  became_ready_count: number;
  still_not_ready_count: number;
  failed_count: number;
  applied_limit: number;
  samples: Array<{
    session_id: string;
    member_id: string;
    outcome: string;
    reason?: string;
    error?: string;
  }>;
}

export interface MemberReflectionBackfillReviewResponse {
  summary: MemberReflectionBackfillSummary;
  members: MemberReflectionBackfillMemberRow[];
  sample: MemberReflectionBackfillSessionRow[];
}

export interface MemberReflectionBackfillRun {
  id: string;
  status: string;
  requested_by?: string;
  filter_scope: MemberReflectionBackfillScope;
  limit: number;
  total_items: number;
  eligible_items: number;
  pending_items: number;
  queued_items: number;
  running_items: number;
  completed_items: number;
  failed_items: number;
  blocked_items: number;
  already_satisfied_items: number;
  created_at: string;
  updated_at: string;
  completed_at?: string;
}

export interface MemberReflectionBackfillRunItem {
  id: string;
  run_id: string;
  member_id: string;
  session_id: string;
  latest_session: boolean;
  status: string;
  eligibility_status: string;
  readiness_version?: number;
  input_set_hash?: string;
  job_id?: string;
  artifact_id?: string;
  blocked_reason?: string;
  error_message?: string;
  created_at: string;
  updated_at: string;
  last_checked_at: string;
  completed_at?: string;
}

export interface MemberReflectionBackfillRunResponse {
  run: MemberReflectionBackfillRun;
  items: MemberReflectionBackfillRunItem[];
}

export interface MemberReflectionBackfillPreviousFailuresResponse {
  items: MemberReflectionBackfillRunItem[];
  count: number;
}

export interface MemberReflectionBackfillPreviousFailuresRetryResponse {
  items: MemberReflectionBackfillRunItem[];
  runs: MemberReflectionBackfillRun[];
  retryable_reset_count: number;
}

export interface MemberReflectionBackfillPreviousFailuresRetryRequest {
  items: Array<Pick<MemberReflectionBackfillRunItem, 'id' | 'status' | 'updated_at'>>;
}

export type MemberReflectionBackfillCreateRunRequest = MemberReflectionBackfillScope;

function queryParams(scope: MemberReflectionBackfillScope = {}): string {
  const params = new URLSearchParams();
  if (scope.limit !== undefined) params.set('limit', String(scope.limit));
  if (scope.member_ids?.length) params.set('member_ids', scope.member_ids.join(','));
  if (scope.session_ids?.length) params.set('session_ids', scope.session_ids.join(','));
  const query = params.toString();
  return query ? `?${query}` : '';
}

export const memberReflectionBackfillApi = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    getMemberReflectionBackfillSummary: builder.query<
      MemberReflectionBackfillSummary,
      MemberReflectionBackfillScope | undefined
    >({
      query: (scope) => `/admin/member-reflection-backfill/summary${queryParams(scope || {})}`,
      providesTags: ['Sessions'],
    }),
    getMemberReflectionBackfillMembers: builder.query<
      MemberReflectionBackfillMemberRow[],
      MemberReflectionBackfillScope | undefined
    >({
      query: (scope) => `/admin/member-reflection-backfill/members${queryParams(scope || {})}`,
      providesTags: ['Sessions'],
    }),
    getMemberReflectionBackfillMemberDetail: builder.query<
      MemberReflectionBackfillSessionRow[],
      string
    >({
      query: (memberId) => `/admin/member-reflection-backfill/members/${memberId}`,
      providesTags: ['Sessions'],
    }),
    reviewMemberReflectionBackfill: builder.mutation<
      MemberReflectionBackfillReviewResponse,
      MemberReflectionBackfillScope
    >({
      query: (body) => ({ url: '/admin/member-reflection-backfill/review', method: 'POST', body }),
    }),
    getMemberReflectionBackfillReadinessGaps: builder.query<
      MemberReflectionBackfillReadinessGapResponse,
      MemberReflectionBackfillScope | undefined
    >({
      query: (scope) =>
        `/admin/member-reflection-backfill/readiness-gaps${queryParams(scope || {})}`,
      providesTags: ['Sessions'],
    }),
    prepareMemberReflectionBackfillReadiness: builder.mutation<
      MemberReflectionBackfillPrepareResponse,
      MemberReflectionBackfillPrepareRequest
    >({
      query: (body) => ({
        url: '/admin/member-reflection-backfill/readiness/prepare',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['Sessions'],
    }),
    createMemberReflectionBackfillRun: builder.mutation<
      MemberReflectionBackfillRunResponse,
      MemberReflectionBackfillCreateRunRequest
    >({
      query: (body) => ({ url: '/admin/member-reflection-backfill/runs', method: 'POST', body }),
      invalidatesTags: ['Sessions'],
    }),
    getMemberReflectionBackfillRun: builder.query<MemberReflectionBackfillRunResponse, string>({
      query: (runId) => `/admin/member-reflection-backfill/runs/${runId}`,
      providesTags: ['Sessions'],
    }),
    getMemberReflectionBackfillPreviousFailures: builder.query<
      MemberReflectionBackfillPreviousFailuresResponse,
      MemberReflectionBackfillScope | undefined
    >({
      query: (scope) =>
        `/admin/member-reflection-backfill/previous-failures${queryParams(scope || {})}`,
      providesTags: ['Sessions'],
    }),
    retryMemberReflectionBackfillRun: builder.mutation<MemberReflectionBackfillRunResponse, string>(
      {
        query: (runId) => ({
          url: `/admin/member-reflection-backfill/runs/${runId}/retry`,
          method: 'POST',
        }),
        invalidatesTags: ['Sessions'],
      }
    ),
    retryMemberReflectionBackfillPreviousFailures: builder.mutation<
      MemberReflectionBackfillPreviousFailuresRetryResponse,
      MemberReflectionBackfillPreviousFailuresRetryRequest
    >({
      query: (body) => ({
        url: '/admin/member-reflection-backfill/previous-failures/retry',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['Sessions'],
    }),
  }),
});

export const {
  useGetMemberReflectionBackfillSummaryQuery,
  useGetMemberReflectionBackfillMembersQuery,
  useGetMemberReflectionBackfillMemberDetailQuery,
  useReviewMemberReflectionBackfillMutation,
  useGetMemberReflectionBackfillReadinessGapsQuery,
  usePrepareMemberReflectionBackfillReadinessMutation,
  useCreateMemberReflectionBackfillRunMutation,
  useGetMemberReflectionBackfillRunQuery,
  useGetMemberReflectionBackfillPreviousFailuresQuery,
  useRetryMemberReflectionBackfillRunMutation,
  useRetryMemberReflectionBackfillPreviousFailuresMutation,
} = memberReflectionBackfillApi;
